import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { AlertComponent } from "@components/alert/alert.component";
import { ButtonComponent } from "@components/button/button.component";
import { InfoComponent } from "@components/info/info.component";
import { CheckboxToggleComponent } from "@components/checkbox-side/checkbox-toggle.component";
import { ConfirmModalComponent } from "@components/confirm-modal/confirm-modal.component";
import { LinkComponent } from "@components/link/link.component";
import { RadioComponent } from "@components/radio/radio.component";
import { SelectComponent } from "@components/select/select.component";
import { ColumnConfig, TableComponent } from "@components/table/table.component";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { EmployeeUpdateRequest } from "app/interfaces/employee.interface";
import { ApiService, UserInfo } from "app/services/api.service";

type RowAction = {
        label?: string;
        action: string;
        iconSvg?: string;
        visible?: (item: any) => boolean;
};

@Component({
    selector: 'app-employees',
    templateUrl: './employees.component.html',
    styleUrl: './employees.component.scss',
    imports: [
        CommonModule,
        TranslateModule,
        FormsModule,
        SelectComponent,
        ButtonComponent,
        CheckboxToggleComponent,
        LinkComponent,
        TableComponent,
        ConfirmModalComponent,
        RadioComponent,
        AlertComponent,
        InfoComponent
    ],
    standalone: true,
})
export class EmployeesComponent implements OnInit {
    userInfo: UserInfo

    state: string = ''
    rol: string = ''
    email: string = ''
    hasWeAccess: boolean = false
    hasEcosentinoAccess: boolean = false
    hasFinancialPortalAccess: boolean = false
    hasNexusAccess: boolean = false
    notificationsFromLeads: boolean = false
    pointsExchangeMethodByEmployee: boolean = false

    originalUserInfo: any[] = []
    originalPointsExchangeMethod: boolean = false

    hasChanges: boolean = false

    rowActions: RowAction[] = [
        {
            action: 'edit',
            iconSvg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.625 20L10.625 18H21V20H8.625ZM4 19H5.075L14.975 9.1L13.9 8.025L4 17.925V19ZM17.1 8.375L14.6 5.9L15.825 4.675C16.025 4.475 16.2667 4.375 16.55 4.375C16.8333 4.375 17.0667 4.475 17.25 4.675L18.325 5.75C18.525 5.93333 18.625 6.16667 18.625 6.45C18.625 6.73333 18.5333 6.96667 18.35 7.15L17.1 8.375ZM16.4 9.1L5.5 20H3V17.5L13.9 6.6L16.4 9.1Z" fill="#365BCA"/>
                      </svg>`,
        },
        {
            action: 'delete',
            iconSvg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7.625 20.0001C7.175 20.0001 6.79167 19.8418 6.475 19.5251C6.15833 19.2084 6 18.8251 6 18.3751V6.0001H5V5.0001H9V4.2251H15V5.0001H19V6.0001H18V18.3751C18 18.8418 17.846 19.2294 17.538 19.5381C17.2293 19.8461 16.8417 20.0001 16.375 20.0001H7.625ZM17 6.0001H7V18.3751C7 18.5584 7.05833 18.7084 7.175 18.8251C7.29167 18.9418 7.44167 19.0001 7.625 19.0001H16.375C16.5417 19.0001 16.6873 18.9374 16.812 18.8121C16.9373 18.6874 17 18.5418 17 18.3751V6.0001ZM9.8 17.0001H10.8V8.0001H9.8V17.0001ZM13.2 17.0001H14.2V8.0001H13.2V17.0001ZM7 6.0001V19.0001V18.3751V6.0001Z" fill="#365BCA"/>
                      </svg>`,
        },
    ]

    tableColumns: ColumnConfig[] = []

    employees: any[] = []

    isConfirmModalVisible: boolean = false
    isSuccessModalVisible: boolean = false
    contactToDelete: any = null

    constructor(private api: ApiService, private translate: TranslateService, private router: Router) {}

    ngOnInit(): void {
        this.api.userInfo.pipe().subscribe((userInfo) => {
            this.userInfo = userInfo

            const baseColumns: ColumnConfig[] = [
                { key: 'state', header: 'my_profile.my_employees.employee_table.status', format: 'text' },
                { key: 'rol', header: 'my_profile.my_employees.employee_table.role', format: 'text' },
                { key: 'email', header: 'my_profile.my_employees.employee_table.email', format: 'text' },
            ]
            const actionColumn: ColumnConfig = { key: 'actions', header: 'my_profile.my_employees.employee_table.actions', format: 'action' }

            const weColumn: ColumnConfig = { key: 'we_access', header: 'my_profile.my_employees.employee_table.access_we_professionals', format: 'toggle' }
            const ecosentinoColumn: ColumnConfig = { key: 'ecosentino_access', header: 'my_profile.my_employees.employee_form.access_e_cosentino', format: 'toggle' }
            const financialColumn: ColumnConfig = { key: 'financial_portal_access', header: 'my_profile.my_employees.employee_form.access_financial_portal', format: 'toggle' }
            const notificationsColumn: ColumnConfig = {
                key: 'we_clients_notifications',
                header: 'my_profile.my_employees.employee_form.notifications_from_leads',
                format: 'toggle',
            }
            const nexusColumn: ColumnConfig = { key: 'cnexus', header: 'my_profile.my_employees.employee_form.nexus_access', format: 'toggle' }
            const actiosColumn: ColumnConfig = { key: 'enabledGlobalRewards', header: 'my_profile.canjear_puntos', format: 'toggle' }
            this.pointsExchangeMethodByEmployee = this.userInfo.pointsExchangeMethodByEmployee ?? false
            this.originalPointsExchangeMethod = this.pointsExchangeMethodByEmployee

            switch (this.userInfo?.accountType) {
                case 'FABRICATOR':
                    this.tableColumns = [
                        ...baseColumns,
                        weColumn,
                        ecosentinoColumn,
                        financialColumn,
                        ...(this.userInfo?.isActiveLeadsB2C ? [notificationsColumn] : []),
                        ...(this.userInfo?.isActiveNexusProgram ? [nexusColumn] : []),
                        actionColumn,
                    ]
                    break
                case 'INSTALLER':
                    this.tableColumns = [...baseColumns, weColumn, ecosentinoColumn, financialColumn, actionColumn]
                    break
                case 'RETAIL':
                    this.tableColumns = [
                        ...baseColumns,
                        weColumn,
                        ...(this.userInfo?.isActiveLeadsB2C ? [notificationsColumn] : []),
                        ...(this.userInfo?.isActiveNexusProgram ? [nexusColumn] : []),
                        actiosColumn,
                        actionColumn,
                    ]
                    break
                case 'ARCHITECT':
                case 'DESIGNER':
                    this.tableColumns = this.userInfo?.role === 'ADMIN' ? [...baseColumns, actionColumn] : [...baseColumns]
                    break
                default:
                    this.tableColumns = [...baseColumns, actionColumn]
                    break
            }

            if (this.userInfo?.role !== 'ADMIN') {
                this.rowActions = [
                    {
                        action: 'edit',
                        iconSvg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8.625 20L10.625 18H21V20H8.625ZM4 19H5.075L14.975 9.1L13.9 8.025L4 17.925V19ZM17.1 8.375L14.6 5.9L15.825 4.675C16.025 4.475 16.2667 4.375 16.55 4.375C16.8333 4.375 17.0667 4.475 17.25 4.675L18.325 5.75C18.525 5.93333 18.625 6.16667 18.625 6.45C18.625 6.73333 18.5333 6.96667 18.35 7.15L17.1 8.375ZM16.4 9.1L5.5 20H3V17.5L13.9 6.6L16.4 9.1Z" fill="#365BCA"/>
                            </svg>`,
                        visible: (item) => item.rol === 'User',
                    },
                ]
            }
        })

        this.api.getEmployees().subscribe({
            next: (employees) => {
                this.employees = employees.map((e) => ({
                    ...e,
                    state: `${e.firstName} ${e.lastName}`,
                    rol: e.primary ? 'Admin' : 'User',
                    we_access: e.active,
                    can_update_we_access: this.userInfo.role == 'ADMIN' && e.email !== this.userInfo?.email,
                    enabledGlobalRewards: e.enabledGlobalRewards,
                    ecosentino_access: e.enabledEcosentino,
                    financial_portal_access: e.enabledFinancialPortal,
                    we_clients_notifications: e.receiveLeads,
                    cnexus: e.cnexus,
                    actions: '',
                }))
                this.originalUserInfo = JSON.parse(JSON.stringify(this.employees))
            },
            error: (err) => console.error('Error al cargar empleados', err),
        })
    }

    onAddEmployee() {
        this.router.navigate(['/employee'])
    }

    onRowAction(event: { action: string; item: any }) {
        if (this.userInfo?.role === 'ADMIN') {
            if (event.action === 'edit') {
                this.router.navigate(['/employee'], { state: { employee: event.item } })
            } else if (event.action === 'delete') {
                this.contactToDelete = event.item
                this.isConfirmModalVisible = true
            }
        } else {
            if (event.action === 'edit' && event.item.rol === 'User') {
                this.router.navigate(['/employee'], { state: { employee: event.item } })
            } else {
                console.warn('No tienes permisos para esta acción')
            }
        }
    }

    onConfirmDelete() {
        this.api.deleteEmployee(this.contactToDelete.id).subscribe(() => {
            this.employees = this.employees.filter((e) => e.id !== this.contactToDelete.id)
            this.isConfirmModalVisible = false
            this.isSuccessModalVisible = true
        })
    }

    closeSuccessModal() {
        this.isSuccessModalVisible = false
        this.contactToDelete = null
    }

    checkForChanges() {
        const employeesChanged = JSON.stringify(this.employees) !== JSON.stringify(this.originalUserInfo)
        const pointsMethodChanged = this.pointsExchangeMethodByEmployee !== this.originalPointsExchangeMethod
        this.hasChanges = employeesChanged || pointsMethodChanged
    }

    onEmployeeToggle(event: { item: any; key: string; value: boolean }) {
        event.item[event.key] = event.value

        this.checkForChanges()
    }

    onSaveAll() {
        const updates: EmployeeUpdateRequest[] = this.employees
            .filter((e, index) => JSON.stringify(e) !== JSON.stringify(this.originalUserInfo[index]))
            .map((e) => ({
                id: e.id,
                firstName: e.state.split(' ')[0],
                lastName: e.state.split(' ')[1] ?? '',
                email: e.email,
                phone: e.phone,
                contactType: e.contactType,
                primary: e.rol === 'Admin',
                active: e.we_access,
                enabledGlobalRewards: e.enabledGlobalRewards,
                enabledEcosentino: e.ecosentino_access,
                enabledFinancialPortal: e.financial_portal_access,
                receiveLeads: e.we_clients_notifications,
                cnexus: e.cnexus,
            }))

        const pointsMethodChanged = this.pointsExchangeMethodByEmployee !== this.originalPointsExchangeMethod

        if (updates.length === 0 && !pointsMethodChanged) {
            console.log('No hay cambios que guardar')
            return
        }

        if (pointsMethodChanged) {
            this.api.enableActiosMode(this.pointsExchangeMethodByEmployee).subscribe({
                next: () => {
                    const updated = { ...this.userInfo, pointsExchangeMethodByEmployee: this.pointsExchangeMethodByEmployee }
                    this.api.updateUserInfo(updated)
                    this.originalPointsExchangeMethod = this.pointsExchangeMethodByEmployee
                    this.checkForChanges()
                },
                error: (err) => console.error('Error al actualizar método de canje', err),
            })
        }

        if (updates.length > 0) {
            this.api.updateEmployees(updates).subscribe({
                next: (updated) => {
                    this.employees = updated.map((e) => ({
                        id: e.id,
                        state: `${e.firstName} ${e.lastName}`,
                        rol: e.primary ? 'Admin' : 'User',
                        email: e.email,
                        we_access: e.active,
                        can_update_we_access: this.userInfo.role == 'ADMIN' && e.email !== this.userInfo?.email,
                        enabledGlobalRewards: e.enabledGlobalRewards,
                        ecosentino_access: e.enabledEcosentino,
                        financial_portal_access: e.enabledFinancialPortal,
                        we_clients_notifications: e.receiveLeads,
                        cnexus: e.cnexus,
                        actions: '',
                    }))

                    this.originalUserInfo = JSON.parse(JSON.stringify(this.employees))
                    this.checkForChanges()
                },
                error: (err) => console.error('Error al actualizar empleados', err),
            })
        }
    }
    onChangePointsExchangeMethod(method: boolean) {
        this.pointsExchangeMethodByEmployee = method
        this.checkForChanges()
    }
}
